import { useQuery, useQueryClient, hashKey } from '@tanstack/vue-query';
import { api } from '/app/plugins/services';
import { useAuth } from '/app/composables/auth';
import { Session } from '/app/utils/auth';
import Storage from '/app/utils/storage';
import { computed, toValue } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';

export const USER_PREFERENCES_KEY = ['preferences', 'user'];
export const PLATFORM_PREFERENCES_KEY = ['preferences', 'platform'];
export const SAVE_PREFERENCE_KEY = ['preferences', 'save'];

export const userPreferencesKey = (ownerId) => [...USER_PREFERENCES_KEY, ownerId];
export const platformPreferencesKey = (platformId) => [...PLATFORM_PREFERENCES_KEY, platformId];

// Data is stamped with its fetch time. A copy the query persister restores
// from an earlier visit predates this page load, so it can be shown but must
// not be written back - see `isFetchedThisLoad`.
const PAGE_LOADED_AT = Date.now();

export const isFetchedThisLoad = (result) => (result?.fetchedAt ?? 0) >= PAGE_LOADED_AT;

// A refetch that lands while a save is pending would replace its optimistic
// value with the server's older copy. Pending saves (queued ones included)
// are re-applied in the order they were made, so they stay on screen until
// each one settles.
const withPendingSaves = (queryClient, queryKey, data) => {
    const hash = hashKey(queryKey);
    const pending = queryClient.getMutationCache()
        .findAll({ mutationKey: SAVE_PREFERENCE_KEY, status: 'pending' })
        .filter((mutation) => hashKey(mutation.state.variables.queryKey) === hash);

    if (!pending.length) return data;

    const next = cloneDeep(data);

    pending.forEach(({ state: { variables: { path, value } } }) => set(next, path, value));

    return next;
};

export const useUserPreferences = () => {
    const { auth } = useAuth();
    const queryClient = useQueryClient();

    // Until `/me` answers, fall back to the id app-init stored on the last
    // sign-in, so the persisted copy restores on boot instead of after `/me`.
    // A different account resolves to a different key, never to someone
    // else's preferences.
    const owner = computed(() => auth.user?.id || Storage.get('user')?.id);
    const queryKey = computed(() => userPreferencesKey(owner.value));

    const query = useQuery({
        queryKey,
        queryFn: async ({ queryKey: fetchedKey }) => {
            const { data: response } = await api.me.preferences();

            return {
                data: withPendingSaves(queryClient, fetchedKey, response.data ?? {}),
                relationships: response.relationships ?? {},
                fetchedAt: Date.now(),
            };
        },
        enabled: computed(() => !!owner.value && Session.exists()),
    });

    return { ...query, queryKey };
};

export const usePlatformPreferences = (platformId) => {
    const queryClient = useQueryClient();
    const queryKey = computed(() => platformPreferencesKey(toValue(platformId)));

    const query = useQuery({
        queryKey,
        queryFn: async ({ queryKey: fetchedKey }) => {
            const { data: response } = await api.platform.preferences(fetchedKey.at(-1));

            return {
                data: withPendingSaves(queryClient, fetchedKey, response.data ?? {}),
                fetchedAt: Date.now(),
            };
        },
        enabled: computed(() => !!toValue(platformId)),
    });

    return { ...query, queryKey };
};
